import {
  Alert,
  Box,
  Button,
  Collapse,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useReserveTerminalsMutation } from "Api/Mutations/Merchants/useReserveTerminalsMutation";
import { useGetHunterTerminalsForReservationQuery } from "Api/Queries/Merchants/useGetHunterTerminalsForReservationQuery";
import { TerminalSelectionPanel } from "Components/Shared/Merchant/Products/AppProducts/ProductAccordion/AccordionContent/ProductActivationForm/TerminalSelectionPanel";
import type {
  ApiProblemDetails,
  MerchantFacilityDto,
} from "Infrastructure/Api/Api";
import { getFormattedAddress } from "Utils/AddressUtils";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Components.Merchant.OrbitL.FacilityTerminals;

const getReserveErrorResource = (error: unknown) => {
  const apiErrors = (error as { data?: ApiProblemDetails })?.data?.errors;
  if (apiErrors && "TerminalInventoryItem" in apiErrors) {
    return RESOURCES.InsufficientStock;
  }
  if (apiErrors && "TerminalCount" in apiErrors) {
    return RESOURCES.CountsLocked;
  }
  return RESOURCES.SaveError;
};
const TERMINAL_RESOURCES =
  Resources.Components.Merchant.OrbitL.TerminalSelection;

type Props = {
  facility: MerchantFacilityDto;
  merchantPublicID: string;
  isHunterFinanced: boolean;
  hasOrbitLProduct: boolean;
  hasSoftPosProduct: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  /**
   * Post-submit: the count input becomes a draft for the change request the section sends.
   * Saving (and with it reserving terminals) is off — support applies the agreed count.
   */
  isCountLocked?: boolean;
  /** Locked order without SoftPOS: the count field is shown so the request can add the product. */
  canRequestSoftPos?: boolean;
  onDraftCountChange?: (count: number) => void;
  onDraftSoftPosCountChange?: (count: number) => void;
};

export const FacilityTerminalCard = ({
  facility,
  merchantPublicID,
  isHunterFinanced,
  hasOrbitLProduct,
  hasSoftPosProduct,
  isExpanded,
  onToggle,
  isCountLocked = false,
  canRequestSoftPos = hasSoftPosProduct,
  onDraftCountChange,
  onDraftSoftPosCountChange,
}: Props) => {
  const { t } = useResource();
  const [terminalCount, setTerminalCount] = useState<number>(
    facility.terminalCount ?? 0,
  );
  // The field keeps its own text so it can be cleared; an empty box counts as zero until the user
  // types. Bound straight to the number, "" snapped back to 0 and the next digit landed as "01".
  const [terminalCountInput, setTerminalCountInput] = useState(
    String(facility.terminalCount ?? 0),
  );
  // SoftPOS licences are only counted here; nothing is reserved from stock for them.
  const [softPosCount, setSoftPosCount] = useState<number>(
    facility.softPosCount ?? 0,
  );
  const [softPosCountInput, setSoftPosCountInput] = useState(
    String(facility.softPosCount ?? 0),
  );
  const [selectedTerminalIDs, setSelectedTerminalIDs] = useState<Array<string>>(
    [],
  );

  const {
    data: terminalsData,
    isLoading: isLoadingTerminals,
    isError: isTerminalsError,
  } = useGetHunterTerminalsForReservationQuery(
    merchantPublicID,
    facility.publicID,
  );

  const { mutate: reserveTerminals, isPending: isReserving } =
    useReserveTerminalsMutation({
      onSuccess: () => {
        toast.success(t(RESOURCES.SaveSuccess));
        setSelectedTerminalIDs([]);
      },
      onError: (error: unknown) =>
        toast.error(t(getReserveErrorResource(error))),
    });

  const assignedTerminals = terminalsData?.assignedTerminals ?? [];
  const allTerminals = [
    ...assignedTerminals,
    ...(terminalsData?.terminals ?? []),
  ];
  const assignedTerminalIDs = assignedTerminals.map(
    (terminal) => terminal.publicID,
  );
  const alreadyAssignedCount = terminalsData?.alreadyAssignedCount ?? 0;

  const remainingToReserve = terminalCount - alreadyAssignedCount;
  const isFullyReserved = alreadyAssignedCount >= terminalCount;
  const hasTerminalCountChanged =
    terminalCount !== (facility.terminalCount ?? 0);
  const hasSoftPosCountChanged = softPosCount !== (facility.softPosCount ?? 0);
  const hasNewSelections = selectedTerminalIDs.length > 0;
  const needsTerminalSelection = isHunterFinanced && remainingToReserve > 0;
  const hasConfiguredTerminals = terminalCount > 0;
  const isReservationComplete =
    isFullyReserved && hasConfiguredTerminals && !hasTerminalCountChanged;
  const canSelectTerminals =
    isHunterFinanced &&
    hasConfiguredTerminals &&
    !isFullyReserved &&
    !isCountLocked;
  const hasAvailableTerminals = allTerminals.length > 0;
  const isHunterStockEmpty =
    canSelectTerminals && !isLoadingTerminals && !hasAvailableTerminals;
  const canSave =
    !isCountLocked &&
    terminalCount >= 0 &&
    softPosCount >= 0 &&
    (hasTerminalCountChanged || hasSoftPosCountChanged || hasNewSelections) &&
    (!needsTerminalSelection ||
      selectedTerminalIDs.length === remainingToReserve);

  const handleTerminalCountChange = useCallback(
    (value: number) => {
      const newCount = Math.max(0, value);
      setTerminalCount(newCount);
      onDraftCountChange?.(newCount);

      const currentlyAssignedCount = assignedTerminalIDs.length;
      if (newCount < currentlyAssignedCount + selectedTerminalIDs.length) {
        setSelectedTerminalIDs((prev) =>
          prev.slice(0, Math.max(0, newCount - currentlyAssignedCount)),
        );
      }
    },
    [
      assignedTerminalIDs.length,
      selectedTerminalIDs.length,
      onDraftCountChange,
    ],
  );

  const handleSave = () => {
    reserveTerminals({
      merchantPublicID,
      facilityPublicID: facility.publicID,
      terminalCount,
      softPosCount,
      terminalPublicIDs: isHunterFinanced ? selectedTerminalIDs : null,
    });
  };

  const addressText =
    (facility.address && getFormattedAddress(facility.address)) ||
    t(RESOURCES.NoAddress);

  const physicalStatusLabel =
    terminalCount > 0
      ? t(RESOURCES.TerminalStatus, {
          assigned: alreadyAssignedCount,
          total: terminalCount,
        })
      : t(RESOURCES.NoTerminalsConfigured);
  const softPosStatusLabel = t(RESOURCES.SoftPosStatus, {
    count: softPosCount,
  });
  const statusLabel = [
    hasOrbitLProduct ? physicalStatusLabel : null,
    hasSoftPosProduct ? softPosStatusLabel : null,
  ]
    .filter((part) => part !== null)
    .join(", ");

  return (
    <ListItem
      disablePadding
      sx={{
        flexDirection: "column",
        alignItems: "stretch",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        mb: 1,
      }}
    >
      <ListItemButton onClick={onToggle} sx={{ py: 1 }}>
        <ListItemText
          primary={facility.name}
          secondary={addressText}
          primaryTypographyProps={{ fontWeight: 500, fontSize: "0.9rem" }}
          secondaryTypographyProps={{ fontSize: "0.8rem" }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
          {statusLabel}
        </Typography>
        <IconButton size="small" edge="end">
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </ListItemButton>

      <Collapse in={isExpanded}>
        <Box sx={{ px: 2, pb: 2 }}>
          <Stack gap={2}>
            {hasOrbitLProduct && (
              <TextField
                label={t(RESOURCES.TerminalCountLabel)}
                helperText={t(RESOURCES.TerminalCountHelperText)}
                type="number"
                size="small"
                value={terminalCountInput}
                onChange={(e) => {
                  setTerminalCountInput(e.target.value);
                  const parsedCount = Number.parseInt(e.target.value, 10);
                  handleTerminalCountChange(
                    Number.isNaN(parsedCount) ? 0 : parsedCount,
                  );
                }}
                onBlur={() => setTerminalCountInput(String(terminalCount))}
                slotProps={{ htmlInput: { min: 0 } }}
                sx={{ maxWidth: 200 }}
              />
            )}
            {canRequestSoftPos && (
              <TextField
                label={t(RESOURCES.SoftPosCountLabel)}
                helperText={t(
                  hasSoftPosProduct
                    ? RESOURCES.SoftPosCountHelperText
                    : RESOURCES.SoftPosRequestHelperText,
                )}
                type="number"
                size="small"
                value={softPosCountInput}
                onChange={(e) => {
                  setSoftPosCountInput(e.target.value);
                  const parsedCount = Number.parseInt(e.target.value, 10);
                  const nextCount = Math.max(
                    0,
                    Number.isNaN(parsedCount) ? 0 : parsedCount,
                  );
                  setSoftPosCount(nextCount);
                  onDraftSoftPosCountChange?.(nextCount);
                }}
                onBlur={() => setSoftPosCountInput(String(softPosCount))}
                slotProps={{ htmlInput: { min: 0 } }}
                sx={{ maxWidth: 200 }}
              />
            )}

            {isTerminalsError && (
              <Alert severity="error">{t(RESOURCES.LoadError)}</Alert>
            )}

            {isReservationComplete && (
              <Alert severity="success">{t(RESOURCES.FullyReserved)}</Alert>
            )}

            {assignedTerminals.length > 0 && (
              <Stack gap={0.5}>
                <Typography variant="body2" fontWeight={500}>
                  {t(RESOURCES.AssignedTerminals)}
                </Typography>
                {assignedTerminals.map((terminal) => (
                  <Typography
                    key={terminal.publicID}
                    variant="body2"
                    color="text.secondary"
                    sx={{ pl: 1 }}
                  >
                    {terminal.serialNumber}
                    {terminal.hardwareModel && ` (${terminal.hardwareModel})`}
                  </Typography>
                ))}
              </Stack>
            )}

            {canSelectTerminals && hasAvailableTerminals && (
              <>
                <Typography variant="body2" fontWeight={500}>
                  {t(TERMINAL_RESOURCES.Title)}
                </Typography>
                <TerminalSelectionPanel
                  terminals={allTerminals}
                  selectedTerminalIDs={[
                    ...assignedTerminalIDs,
                    ...selectedTerminalIDs,
                  ]}
                  assignedTerminalIDs={assignedTerminalIDs}
                  requiredCount={terminalCount}
                  isLoading={isLoadingTerminals}
                  onSelectionChange={(ids) =>
                    setSelectedTerminalIDs(
                      ids.filter((id) => !assignedTerminalIDs.includes(id)),
                    )
                  }
                />
              </>
            )}

            {isHunterStockEmpty && (
              <Alert severity="warning">
                {t(TERMINAL_RESOURCES.NoTerminalsAvailable)}
              </Alert>
            )}

            {canSave && (
              <Button
                variant="contained"
                size="small"
                onClick={handleSave}
                disabled={isReserving}
                sx={{ alignSelf: "flex-start" }}
              >
                {isReserving ? t(RESOURCES.Saving) : t(RESOURCES.SaveButton)}
              </Button>
            )}
          </Stack>
        </Box>
      </Collapse>
    </ListItem>
  );
};
